import { useEffect, useState, useRef } from "react";
import { getFulfillmentDateTime, getFilteringDateTime } from "../utils/sortCateringOrder";
import { CateringOrder } from "../../../shared/models/CateringOrder";

export const useReminders = (orders: CateringOrder[] | undefined) => {
  const [todayOrders, setTodayOrders] = useState<CateringOrder[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const sessionShownRef = useRef<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string | undefined>(undefined);

  // Compute today's orders on data load
  useEffect(() => {
    if (!orders) return;

    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfDay = new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000);

    const todays = orders.filter((o) => {
      const fdt = getFilteringDateTime(o);
      return fdt && fdt >= startOfDay && fdt < endOfDay;
    });

    // Sort by fulfillment time ascending
    todays.sort((a, b) => {
      const fdtA = getFulfillmentDateTime(a);
      const fdtB = getFulfillmentDateTime(b);
      if (!fdtA || !fdtB) return 0;
      return fdtA.getTime() - fdtB.getTime();
    });

    setTodayOrders(todays);

    // Show modal once per session after login if there are today's orders
    if (!sessionShownRef.current && todays.length > 0) {
      sessionShownRef.current = true;
      setModalTitle("Today's Catering Orders");
      setModalOpen(true);
    }
  }, [orders]);

  // Poll for 2-hour reminders while app is open
  useEffect(() => {
    if (!orders || orders.length === 0) return;

    const msToNextEven2Hour = () => {
      const now = new Date();
      // compute next boundary at minute 0 where hour % 2 === 0
      const next = new Date(now);
      next.setMinutes(0, 0, 0);
      const hour = next.getHours();
      if (hour % 2 === 0) {
        // if we're already at an even hour (exact minute=0), schedule 2 hours ahead
        if (now.getMinutes() === 0 && now.getSeconds() === 0) {
          next.setHours(next.getHours() + 2);
        } else {
          // otherwise next even hour is +1 hour if current hour is odd, or +2 if even
          next.setHours(next.getHours() + 2);
        }
      } else {
        // odd hour -> next even hour is +1
        next.setHours(next.getHours() + 1);
      }
      return Math.max(0, next.getTime() - now.getTime());
    };

    const tick = () => {
      const now = new Date();
      // upcoming orders: same day and filtering datetime >= now
      const upcoming = orders.filter((o) => {
        const fdt = getFilteringDateTime(o);
        if (!fdt) return false;
        return (
          fdt.getFullYear() === now.getFullYear() &&
          fdt.getMonth() === now.getMonth() &&
          fdt.getDate() === now.getDate() &&
          fdt.getTime() >= now.getTime()
        );
      });

      if (upcoming.length > 0) {
        // replace today's list with upcoming orders
        setTodayOrders(upcoming.sort((a, b) => {
          const d1 = getFulfillmentDateTime(a);
          const d2 = getFulfillmentDateTime(b);
          if (!d1 || !d2) return 0;
          return d1.getTime() - d2.getTime();
        }));
        setModalTitle("Today's Upcoming Orders");
        setModalOpen(true);
      }
    };

    // schedule first tick at next even 2-hour boundary, then every 2 hours
    const timeoutRef: any = { current: null };
    const firstDelay = msToNextEven2Hour();
    const timeoutId = setTimeout(() => {
      tick();
      const intervalId = setInterval(tick, 2 * 60 * 60 * 1000);
      // store interval id on the timeout so we can clear both
      timeoutRef.current = intervalId;
    }, firstDelay);

    return () => {
      clearTimeout(timeoutId);
      if (timeoutRef.current) clearInterval(timeoutRef.current);
    };
  }, [orders]);

  const dismissModal = () => setModalOpen(false);

  return { todayOrders, modalOpen, dismissModal, modalTitle };
};
